import { Badge, Card, Hero, Section, StatBar } from '../components/ui';

const certifications = [
  { name: 'SOC 2 Type II', status: 'In Progress', badge: 'upcoming' },
  { name: 'GDPR Compliant', status: 'Certified', badge: 'success' },
  { name: 'ISO 27001', status: 'Planned', badge: 'upcoming' },
  { name: 'HIPAA', status: 'Available', badge: 'success' }
];

const securityFeatures = [
  {
    title: 'Data Encryption',
    description: 'End-to-end encryption for all data in transit and at rest',
    details: [
      'TLS 1.3 for data in transit',
      'AES-256 encryption for data at rest',
      'Encrypted database storage',
      'Secure key management'
    ]
  },
  {
    title: 'Access Controls',
    description: 'Granular permissions and role-based access control',
    details: [
      'Role-based access (Owner, Admin, Manager, Analyst, Reviewer)',
      'Multi-factor authentication support',
      'Session management and timeout',
      'IP whitelisting available'
    ]
  },
  {
    title: 'Data Isolation',
    description: 'Complete multi-tenant architecture with organization-level isolation',
    details: [
      'Dedicated database schemas per organization',
      'Isolated storage buckets',
      'Separate processing queues',
      'Cross-tenant access prevention'
    ]
  },
  {
    title: 'Audit Logging',
    description: 'Comprehensive audit trail of all operations',
    details: [
      'All API calls logged',
      'Document operations tracked',
      'User actions recorded',
      'Exportable audit reports'
    ]
  },
  {
    title: 'Infrastructure Security',
    description: 'Built on Cloudflare\'s global network',
    details: [
      'DDoS protection included',
      'Edge computing for low latency',
      'Automatic failover',
      'Regular security updates'
    ]
  },
  {
    title: 'Data Residency',
    description: 'Control where your data is stored and processed',
    details: [
      'Regional data centers',
      'GDPR-compliant EU storage',
      'US data residency options',
      'Custom location support (Enterprise)'
    ]
  }
];

const practices = [
  {
    title: 'Secure Development',
    items: [
      'Code reviews for all changes',
      'Automated security scanning',
      'Dependency vulnerability monitoring',
      'Regular penetration testing'
    ]
  },
  {
    title: 'Incident Response',
    items: [
      '24/7 security monitoring',
      'Incident response team',
      'Breach notification procedures',
      'Regular security drills'
    ]
  },
  {
    title: 'Data Protection',
    items: [
      'Data retention policies',
      'Secure data deletion',
      'Backup and recovery',
      'Data export capabilities'
    ]
  }
];

const securityStats = [
  { label: 'Platform uptime', value: '99.98%' },
  { label: 'Pen tests per year', value: '4' },
  { label: 'Encryption', value: 'AES-256 / TLS 1.3' }
];

const securityHighlights = [
  { label: 'Data residency options', value: 'US & EU regions' },
  { label: 'Incident response SLA', value: '15 min initial response' },
  { label: 'Access reviews', value: 'Monthly automated audits' }
];

export const Security = () => {
  return (
    <>
      <Hero
        title="Enterprise-Grade Security"
        description="Your document data is protected with industry-leading security practices and compliance standards."
        size="sm"
      >
        <StatBar stats={securityStats} divider />
      </Hero>

      <Section centered>
        <div className="security-highlights">
          {securityHighlights.map((highlight) => (
            <div key={highlight.label} className="security-highlight">
              <span>{highlight.label}</span>
              <span>{highlight.value}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Compliance & Certifications" subtitle="Meeting the highest industry standards" centered>
        <div className="security-certifications">
          {certifications.map((cert) => (
            <Card key={cert.name} elevation="md" className="security-cert-card">
              <div className="security-cert-card__body">
                <h4>{cert.name}</h4>
                <Badge variant={cert.badge === 'success' ? 'success' : 'warning'} appearance="solid">
                  {cert.status}
                </Badge>
              </div>
            </Card>
          ))}
        </div>
      </Section>

      <Section title="Security Features" subtitle="Protecting your data at every layer" background="subtle" centered>
        <div className="security-features">
          {securityFeatures.map((feature) => (
            <Card key={feature.title} elevation="md" className="security-feature-card">
              <div className="security-feature-card__header">
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
              <ul className="security-feature-card__list">
                {feature.details.map((detail) => (
                  <li key={detail}>{detail}</li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      </Section>

      <Section title="Security Practices" subtitle="Our commitment to protecting your data" centered>
        <div className="security-practices">
          {practices.map((practice) => (
            <Card key={practice.title} elevation="md" className="security-practice-card">
              <h4>{practice.title}</h4>
              <ul>
                {practice.items.map((item) => (
                  <li key={item}>
                    <span aria-hidden>✓</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      </Section>

      <Section background="primary" centered>
        <div className="security-commitment">
          <h2>Data Privacy Commitment</h2>
          <p>
            We never sell, share, or use your document data for any purpose other than providing our service to you.
            Your data is yours, and you maintain complete control over it at all times. We provide data export
            capabilities and secure deletion options to ensure you can manage your data according to your requirements.
          </p>
        </div>
      </Section>

      <Section title="Questions About Security?" centered>
        <div className="security-contact">
          <p>Have specific security requirements or questions? Our security team is here to help.</p>
          <Card elevation="md" className="security-contact__card">
            <div>
              <div>Security Team</div>
              <a href="mailto:security@textadapt.com">security@textadapt.com</a>
            </div>
          </Card>
        </div>
      </Section>
    </>
  );
};
